import type { ViewQueryField, ViewQueryList, ViewQueryNode } from '@salescore/core'
import dayjs from 'dayjs'
import { Array, Equivalence, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'

import { formatDatePropertyValue } from '../../../domain/datePropertyValue'
import type { DeepReadonly } from '../../../rsheet/types/DeepReadonly'

/**
 * 新規レコードを作成したときに項目へ入れる値を、項目の型に応じて求める
 * @param meta 項目のメタ情報
 * @returns 項目へ入れる値。設定がなければ undefined
 */
const createInitialValue = (
  meta: Pick<ViewQueryField['meta'], 'defaultValue' | 'fieldType'>,
): string | number | boolean | undefined => {
  const now = dayjs()
  switch (meta.fieldType) {
    case 'date':
    case 'datetime': {
      if (typeof meta.defaultValue !== 'number') {
        return undefined
      }
      const date = now.add(meta.defaultValue, 'day')
      return formatDatePropertyValue({ propertyType: meta.fieldType, date })
    }
    default: {
      return meta.defaultValue
    }
  }
}

export function getDefaultAttributes(
  node: DeepReadonly<ViewQueryNode>,
  query: DeepReadonly<Pick<ViewQueryList, 'fields'>> | undefined,
): {
  defaultValues: Array<{
    field: ViewQueryField
    name: string
    propertySourceName: string
    // eslint-disable-next-line sonarjs/use-type-alias
    value: string | number | boolean
  }>
  recordAttributes: Record<string, string | number | boolean>
  changeAttributes: Record<string, string | number | boolean>
} {
  if (query === undefined) {
    return { defaultValues: [], recordAttributes: {}, changeAttributes: {} }
  }
  const fields = query.fields.filter((x) => Array.makeEquivalence(Equivalence.String)(x.nodePath, node.path))
  const defaultValues = fields
    .map((field) => {
      const value = createInitialValue(field.meta)
      if (value === undefined || field.write?.propertySourceName === undefined) {
        // 入れる値がない項目は undefined を返し、後続の filter で除外する
        // eslint-disable-next-line array-callback-return
        return
      }

      return {
        field,
        name: field.name,
        propertySourceName: field.write.propertySourceName,
        value,
      }
    })
    .filter(isNotNullish)
  const recordAttributes = Record.fromIterableWith(defaultValues, (x) => [x.name, x.value])
  const changeAttributes = Record.fromIterableWith(defaultValues, (x) => [x.propertySourceName, x.value])
  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- DeepReadonlyの辻褄合わせが発生してしまう
    defaultValues: defaultValues as Array<{
      field: ViewQueryField
      name: string
      propertySourceName: string
      value: string | number | boolean
    }>,
    recordAttributes,
    changeAttributes,
  }
}
